import {
  ADVISOR_BOOK_IMPORT_SUCCESS_STATUSES,
  ADVISOR_BOOK_IMPORT_TERMINAL_FAILURE_STATUSES,
} from 'src/features/advisor-book-onboarding/constants/advisor-book-import';
import { z } from 'zod';

const importErrorsSchema = z.record(z.string(), z.unknown());

// GraphQL BigDecimal serializes as a string; coerce to number for sorting/display.
const graphqlBigDecimalSchema = z
  .union([z.number(), z.string()])
  .nullable()
  .transform((value) => {
    if (value === null) {
      return null;
    }

    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  });

const stagedClientSchema = z.object({
  id: z.string(),
  role: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  importErrors: importErrorsSchema,
});

export const transferMethodSchema = z.enum(['transfer_as_is', 'consolidate']);

// Structured beneficiary, mirroring the fields the direct-ECI AccountsPanelQuery selects on
// EciAccountBeneficiary; the confirm-accounts subform rehydrates from this shape.
const beneficiarySchema = z.object({
  designation: z.enum(['primary', 'contingent']).nullable().catch(null),
  kind: z.string().nullable(),
  firstName: z.string().nullable(),
  middleName: z.string().nullable(),
  lastName: z.string().nullable(),
  trustEntityName: z.string().nullable(),
  relationship: z.string().nullable(),
  dateOfBirthOrTrustDate: z.string().nullable(),
  taxId: z.string().nullable(),
  percentage: z.number().nullable(),
  perStirpes: z.boolean().nullable(),
});

// Sidecar sub-entities mirror the fields the direct-ECI AccountsPanelQuery selects, so the
// confirm-accounts subforms rehydrate from the same shape. Enum columns use z.enum (matching the
// generated unions the shared form types expect, so the map needs no cast) with `.catch` — an
// unrecognized backend enum value degrades to null (or drops the stakeholder row) instead of
// failing the whole household parse and blanking the cockpit.
const stagedStandingInstructionSchema = z.object({
  id: z.string(),
  bankName: z.string().nullable(),
  maskedAccountNumber: z.string(),
  shouldCarryOver: z.boolean(),
});

const stagedRecurringTransferSchema = z.object({
  id: z.string(),
  kind: z.string().nullable(),
  frequency: z.string().nullable(),
  amountCents: z.union([z.number(), z.string()]).nullable(),
  dayOfMonth: z.number().nullable(),
  dayOfWeek: z.number().nullable(),
  fromStandingInstructionId: z.string().nullable(),
  toStandingInstructionId: z.string().nullable(),
  shouldCarryOver: z.boolean(),
});

const stagedTrustInformationSchema = z.object({
  entityName: z.string().nullable(),
  taxIdType: z.enum(['ein', 'ssn']).nullable().catch(null),
  taxId: z.string().nullable(),
  countryOfOrganizationCode: z.string().nullable(),
  trustDate: z.string().nullable(),
  trustType: z.enum(['under_agreement', 'under_will']).nullable().catch(null),
  trustRevocability: z.enum(['irrevocable', 'revocable']).nullable().catch(null),
  accreditedInvestor: z.boolean().nullable(),
  registeredBrokerDealer: z.boolean().nullable(),
  registeredInvestmentAdvisor: z.boolean().nullable(),
  registeredInvestmentCompany: z.boolean().nullable(),
  homeAddressLine1: z.string().nullable(),
  homeAddressLine2: z.string().nullable(),
  homeCity: z.string().nullable(),
  homeState: z.string().nullable(),
  homePostalCode: z.string().nullable(),
  mailingAddressSameAsHome: z.boolean().nullable(),
  mailingAddressLine1: z.string().nullable(),
  mailingAddressLine2: z.string().nullable(),
  mailingCity: z.string().nullable(),
  mailingState: z.string().nullable(),
  mailingPostalCode: z.string().nullable(),
});

const stagedEmployerInformationSchema = z.object({
  entityName: z.string(),
  taxId: z.string(),
});

const stagedStakeholderSchema = z.object({
  role: z.enum(['grantor', 'joint_owner', 'primary_owner', 'trustee']),
  user: z.object({ id: z.string() }),
});

const stagedJointOwnerSchema = z.object({
  fullName: z.string().nullable(),
  relationshipToPrimary: z
    .enum(['business_partner', 'child', 'domestic_partner', 'other', 'parent', 'self', 'sibling', 'spouse'])
    .nullable()
    .catch(null),
  ownershipPercentage: z.number().nullable(),
});

const stagedAccountSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  accountName: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  accountType: z.string().nullable(),
  custodian: z.string().nullable(),
  transferMethod: transferMethodSchema.default('transfer_as_is'),
  beneficiaries: z.array(beneficiarySchema).default([]),
  estimatedBalance: graphqlBigDecimalSchema.optional().transform((value) => value ?? null),
  typeGroup: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  registrationType: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  hasTransferOnDeathDesignation: z.boolean().optional().default(false),
  importErrors: importErrorsSchema,
  standingInstructions: z.array(stagedStandingInstructionSchema).default([]),
  recurringTransfers: z.array(stagedRecurringTransferSchema).default([]),
  trustInformation: stagedTrustInformationSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  employerInformation: stagedEmployerInformationSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  accountStakeholders: z
    .array(z.unknown())
    .transform((items) =>
      items.flatMap((item) => {
        const parsed = stagedStakeholderSchema.safeParse(item);
        return parsed.success ? [parsed.data] : [];
      }),
    )
    .catch([]),
  jointOwners: z.array(stagedJointOwnerSchema).default([]),
});

export const stagedHouseholdSchema = z.object({
  id: z.string(),
  uuid: z.string().uuid().optional(),
  draft: z.boolean(),
  householdId: z.string().nullable(),
  status: z.string(),
  externalKey: z.string().nullable(),
  householdName: z.string().nullable(),
  estAum: graphqlBigDecimalSchema,
  importErrors: importErrorsSchema,
  displayName: z.string(),
  // Optional: enrollment list queries omit it; single-household + fix-contact fetch it.
  enrollmentUuid: z.string().optional(),
  blockedReason: z.string().nullish(),
  stagedClients: z.array(stagedClientSchema),
  stagedAccounts: z.array(stagedAccountSchema),
});

export type AdvisorStagedClient = z.infer<typeof stagedClientSchema>;
export type AdvisorStagedAccount = z.infer<typeof stagedAccountSchema>;
export type AdvisorStagedBeneficiary = z.infer<typeof beneficiarySchema>;
export type AdvisorStagedHousehold = z.infer<typeof stagedHouseholdSchema>;
export type AdvisorTransferMethod = z.infer<typeof transferMethodSchema>;

export function isStagedHouseholdEditable(household: AdvisorStagedHousehold) {
  return household.draft;
}

const IRA_ACCOUNT_TYPES = new Set([
  'ira',
  'roth_ira',
  'ira_rollover',
  'contributory_ira',
  'ira_cont',
  'traditional_ira',
  'sep_ira',
  'simple_ira',
]);

/** Mirrors AdvisorOnboarding::AccountTypeNormalizer for sheet labels still stored raw. */
const ACCOUNT_TYPE_NORMALIZE_RULES: readonly { readonly pattern: RegExp; readonly subtype: string }[] = [
  { pattern: /roth/, subtype: 'roth_ira' },
  { pattern: /roll ?over/, subtype: 'ira_rollover' },
  { pattern: /contributory/, subtype: 'contributory_ira' },
  { pattern: /\bira\b|individual retirement/, subtype: 'ira' },
  { pattern: /community/, subtype: 'community_property' },
  { pattern: /jtwros|joint tenant|joint/, subtype: 'jtwros' },
  { pattern: /trust/, subtype: 'trust' },
  { pattern: /\btod\b/, subtype: 'individual_tod' },
  { pattern: /individual|brokerage|taxable|single/, subtype: 'individual' },
];

export function normalizeStagedAccountType(accountType: string | null): string | null {
  if (accountType === null) {
    return null;
  }

  // Stored subtype uses underscores; scrub below turns `_` into spaces, so match before that.
  if (accountType.toLowerCase() === 'individual_tod') {
    return 'individual_tod';
  }

  const normalized = accountType
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  if (normalized === '') {
    return null;
  }

  if (
    IRA_ACCOUNT_TYPES.has(normalized) ||
    normalized === 'individual' ||
    normalized === 'jtwros' ||
    normalized === 'trust'
  ) {
    return normalized;
  }

  for (const rule of ACCOUNT_TYPE_NORMALIZE_RULES) {
    if (rule.pattern.test(normalized)) {
      return rule.subtype;
    }
  }
  return null;
}

export function isIraAccountType(accountType: string | null): boolean {
  const subtype = normalizeStagedAccountType(accountType) ?? accountType?.toLowerCase() ?? null;
  if (subtype === null || subtype === '') {
    return false;
  }
  return IRA_ACCOUNT_TYPES.has(subtype);
}

export function isTodAccountType(accountType: string | null): boolean {
  if (accountType?.toLowerCase() === 'individual_tod') {
    return true;
  }
  const subtype = normalizeStagedAccountType(accountType) ?? accountType?.toLowerCase() ?? null;
  return subtype === 'individual_tod';
}

export function beneficiariesShareTotal(
  beneficiaries: readonly AdvisorStagedBeneficiary[],
  designation: 'primary' | 'contingent' = 'primary',
): number {
  return beneficiaries
    .filter((beneficiary) => beneficiary.designation === designation)
    .reduce((sum, beneficiary) => sum + (beneficiary.percentage ?? 0), 0);
}

export function areBeneficiariesValid(account: AdvisorStagedAccount): boolean {
  if (!isIraAccountType(account.accountType) && !isTodAccountType(account.accountType)) {
    return true;
  }

  const primaries = account.beneficiaries.filter((beneficiary) => beneficiary.designation === 'primary');
  if (primaries.length === 0) {
    return false;
  }

  const namesPresent = primaries.every((beneficiary) =>
    beneficiary.kind === 'trust'
      ? (beneficiary.trustEntityName ?? '').trim() !== ''
      : (beneficiary.firstName ?? '').trim() !== '' && (beneficiary.lastName ?? '').trim() !== '',
  );
  if (!namesPresent) {
    return false;
  }

  // A primary with a null percentage would otherwise contribute 0 to the total, letting a single
  // 100% primary mask a second primary that never had a share entered.
  if (primaries.some((beneficiary) => beneficiary.percentage === null)) {
    return false;
  }

  const total = beneficiariesShareTotal(account.beneficiaries, 'primary');
  return Math.abs(total - 100) < 0.01;
}

export function parseStagedHouseholds(value: unknown): readonly AdvisorStagedHousehold[] {
  const parsed = z.array(stagedHouseholdSchema).safeParse(value);
  return parsed.success ? parsed.data : [];
}

export function getPrimaryClient(household: AdvisorStagedHousehold): AdvisorStagedClient | undefined {
  return household.stagedClients.find((client) => client.role === 'primary');
}

export function getCoClients(household: AdvisorStagedHousehold): readonly AdvisorStagedClient[] {
  return household.stagedClients.filter((client) => client.role === 'co');
}

export function countImportErrors(record: { readonly importErrors: Record<string, unknown> }): number {
  return Object.keys(record.importErrors).length;
}

export function formatImportErrorMessages(importErrors: Record<string, unknown>): readonly string[] {
  return Object.entries(importErrors).flatMap(([field, messages]) => {
    const label = field.replaceAll('_', ' ');
    if (Array.isArray(messages)) {
      return messages.map((message) => `${label}: ${formatImportErrorMessage(message)}`);
    }
    return [`${label}: ${formatImportErrorMessage(messages)}`];
  });
}

export function getImportErrorMessage(importErrors: Record<string, unknown>, field: string): string | undefined {
  const messages = importErrors[field];
  if (messages === undefined) {
    return undefined;
  }
  if (Array.isArray(messages)) {
    const first = messages[0];
    return first === undefined ? undefined : formatImportErrorMessage(first);
  }
  return formatImportErrorMessage(messages);
}

function formatImportErrorMessage(message: unknown) {
  if (typeof message === 'string') {
    return message;
  }
  if (typeof message === 'number' || typeof message === 'boolean') {
    return String(message);
  }
  if (message === null || message === undefined) {
    return 'invalid';
  }
  return 'invalid';
}

export function countHouseholdImportErrors(household: AdvisorStagedHousehold): number {
  let total = countImportErrors(household);
  for (const client of household.stagedClients) {
    total += countImportErrors(client);
  }
  for (const account of household.stagedAccounts) {
    total += countImportErrors(account);
  }
  return total;
}

export function getClientFullName(client: AdvisorStagedClient): string {
  return [client.firstName, client.lastName].filter((part) => part !== null && part !== '').join(' ');
}

export function getAccountNumberLast4(number: string | null): string {
  const trimmed = number?.trim() ?? '';
  if (trimmed === '') {
    return '—';
  }

  return trimmed.slice(-4);
}

export function getHouseholdDisplayName(household: AdvisorStagedHousehold): string {
  const householdName = household.householdName?.trim() ?? '';
  if (householdName !== '') {
    return householdName;
  }

  return household.displayName.trim() !== '' ? household.displayName : '—';
}

export function getHouseholdSortLabel(household: AdvisorStagedHousehold): string {
  return getHouseholdDisplayName(household);
}

function hasEmailContactError(client: AdvisorStagedClient): boolean {
  if (client.importErrors['email'] !== undefined) {
    return true;
  }

  return !isValidEmail(client.email?.trim() ?? '');
}

function hasPhoneContactError(client: AdvisorStagedClient): boolean {
  if (client.importErrors['phone'] !== undefined) {
    return true;
  }

  return !isValidPhone(client.phone?.trim() ?? '');
}

export function countClientContactErrors(client: AdvisorStagedClient): number {
  let errors = countImportErrors(client);
  const emailCountedInImportErrors = client.importErrors['email'] !== undefined;
  const phoneCountedInImportErrors = client.importErrors['phone'] !== undefined;

  if (!emailCountedInImportErrors && hasEmailContactError(client)) {
    errors += 1;
  }
  if (!phoneCountedInImportErrors && hasPhoneContactError(client)) {
    errors += 1;
  }

  return errors;
}

const SHEET_EDITABLE_IMPORT_ERROR_KEYS = ['email', 'phone', 'first_name', 'last_name'] as const;

const NAME_REQUIRED_MESSAGE = 'Enter first and last name';

export function hasSheetEditableNameError(client: AdvisorStagedClient): boolean {
  if (client.importErrors['first_name'] !== undefined || client.importErrors['last_name'] !== undefined) {
    return true;
  }

  const fullName = getClientFullName(client).trim();
  if (fullName === '') {
    return true;
  }

  const parts = fullName.split(/\s+/).filter((part) => part !== '');
  return parts.length < 2;
}

export function getSheetEditableNameErrorMessage(client: AdvisorStagedClient): string | undefined {
  if (!hasSheetEditableNameError(client)) {
    return undefined;
  }
  return NAME_REQUIRED_MESSAGE;
}

export function countSheetEditableClientContactErrors(client: AdvisorStagedClient): number {
  let errors = 0;
  for (const key of SHEET_EDITABLE_IMPORT_ERROR_KEYS) {
    if (client.importErrors[key] !== undefined) {
      errors += 1;
    }
  }

  if (client.importErrors['email'] === undefined && hasEmailContactError(client)) {
    errors += 1;
  }
  if (client.importErrors['phone'] === undefined && hasPhoneContactError(client)) {
    errors += 1;
  }
  if (
    client.importErrors['first_name'] === undefined &&
    client.importErrors['last_name'] === undefined &&
    hasSheetEditableNameError(client)
  ) {
    errors += 1;
  }

  return errors;
}

export function countSheetEditableContactErrors(household: AdvisorStagedHousehold): number {
  let errors = 0;
  for (const client of household.stagedClients) {
    errors += countSheetEditableClientContactErrors(client);
  }
  return errors;
}

export function getContactFieldState(
  client: AdvisorStagedClient,
  field: 'email' | 'phone',
): {
  readonly hasError: boolean;
  readonly isMissing: boolean;
  readonly value: string;
} {
  const value = field === 'email' ? (client.email?.trim() ?? '') : (client.phone?.trim() ?? '');
  const hasError = field === 'email' ? hasEmailContactError(client) : hasPhoneContactError(client);

  return {
    hasError,
    isMissing: value === '',
    value,
  };
}

// Permissive hint aligned with StagedRecordValidator (URI::MailTo::EMAIL_REGEXP);
// server validation is canonical.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') {
    return false;
  }
  return EMAIL_FORMAT.test(trimmed);
}

export function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10;
}

export function countContactErrors(household: AdvisorStagedHousehold): number {
  let errors = 0;
  for (const client of household.stagedClients) {
    errors += countClientContactErrors(client);
  }
  return errors;
}

type StagedBookSummary = {
  readonly householdCount: number;
  readonly clientCount: number;
  readonly accountCount: number;
};

export function summarizeStagedBook(households: readonly AdvisorStagedHousehold[]): StagedBookSummary {
  let clientCount = 0;
  let accountCount = 0;
  for (const household of households) {
    clientCount += household.stagedClients.length;
    accountCount += household.stagedAccounts.length;
  }
  return {
    householdCount: households.length,
    clientCount,
    accountCount,
  };
}

export function isReuploadEditableHousehold(household: AdvisorStagedHousehold) {
  return household.status === 'confirm_account';
}

type ReuploadBookSummary = {
  readonly parsed: number;
  readonly toUpdate: number;
  readonly confirmed: number;
};

export function summarizeReuploadBook(households: readonly AdvisorStagedHousehold[]): ReuploadBookSummary {
  let toUpdate = 0;
  for (const household of households) {
    if (isReuploadEditableHousehold(household)) {
      toUpdate += 1;
    }
  }
  return {
    parsed: households.length,
    toUpdate,
    confirmed: households.length - toUpdate,
  };
}

function statusMatchesList(status: string, statuses: readonly string[]): boolean {
  return statuses.some((candidate) => candidate === status);
}

export function isBulkImportTerminalFailure(status: string): boolean {
  return statusMatchesList(status, ADVISOR_BOOK_IMPORT_TERMINAL_FAILURE_STATUSES);
}

export function isBulkImportSuccess(status: string): boolean {
  return statusMatchesList(status, ADVISOR_BOOK_IMPORT_SUCCESS_STATUSES);
}

export function isBulkImportReadyForReview(status: string): boolean {
  return isBulkImportSuccess(status);
}
